import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import ResizablePanels from '../components/ResizablePanels';
import DashboardLayout from '../components/layout/DashboardLayout';
import { triggerCreditRefresh } from '../components/CreditDisplay';
import { useToast } from '../components/ui/Toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const InterviewPrep = () => {
  const { user, session, getToken, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  // State management
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [activeTab, setActiveTab] = useState('star'); // 'star', 'research', 'practice', 'mock-interview'
  const [loading, setLoading] = useState(true);

  // STAR stories state
  const [stories, setStories] = useState([]);
  const [generatingStories, setGeneratingStories] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Company research state
  const [research, setResearch] = useState(null);
  const [generatingResearch, setGeneratingResearch] = useState(false);

  // Practice walkthrough state
  const [practiceView, setPracticeView] = useState('history'); // 'history', 'active', 'viewing'
  const [sessionHistory, setSessionHistory] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [practiceMessages, setPracticeMessages] = useState([]);
  const [practiceInput, setPracticeInput] = useState('');
  const [practiceSending, setPracticeSending] = useState(false);
  const [totalTime, setTotalTime] = useState(0);
  const [lapTime, setLapTime] = useState(0);
  const [isFinalFeedback, setIsFinalFeedback] = useState(false);
  const [viewingSession, setViewingSession] = useState(null);
  const [resumePdfUrl, setResumePdfUrl] = useState(null);
  const [finalEvaluation, setFinalEvaluation] = useState(null);
  const timerIntervalRef = useRef(null);

  useEffect(() => {
    if (!authLoading && session && user) {
      loadJobs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, session, user]);

  useEffect(() => {
    if (selectedJob) {
      if (activeTab === 'star') {
        loadStories();
      } else if (activeTab === 'research') {
        loadResearch();
      } else if (activeTab === 'practice') {
        loadPracticeSessions();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJob, activeTab]);

  const loadJobs = async () => {
    const token = getToken();
    if (!token) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/interview-prep/jobs`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch interview prep jobs');
      }

      const data = await response.json();
      setJobs(data.jobs || []);

      // Auto-select first job if available
      if (data.jobs && data.jobs.length > 0 && !selectedJob) {
        setSelectedJob(data.jobs[0]);
      }
    } catch (err) {
      showError(`Failed to load jobs: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadStories = async () => {
    if (!selectedJob) return;

    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/interview-prep/applications/${selectedJob.id}/star-stories`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch STAR stories');
      }

      const data = await response.json();
      setStories(data.stories || []);
    } catch (err) {
      setStories([]);
    }
  };

  const generateStories = async () => {
    if (!selectedJob) return;

    const token = getToken();
    if (!token) return;

    try {
      setGeneratingStories(true);
      const response = await fetch(`${API_URL}/api/interview-prep/applications/${selectedJob.id}/star-stories/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate STAR stories');
      }

      const data = await response.json();
      setStories(data.stories || []);

      // Refresh credit display to show updated count
      triggerCreditRefresh();

      // Reload jobs to update status
      await loadJobs();

      showSuccess(`Successfully generated ${data.stories.length} STAR stories!`);
    } catch (err) {
      showError(err.message);
    } finally {
      setGeneratingStories(false);
    }
  };

  const updateStory = async (storyId, updates) => {
    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/interview-prep/star-stories/${storyId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        throw new Error('Failed to update story');
      }

      const data = await response.json();

      // Update local state
      setStories(stories.map(s => s.id === storyId ? data.story : s));
      setEditingStory(null);
      setEditForm({});

      showSuccess('Story updated successfully!');
    } catch (err) {
      showError(`Failed to update story: ${err.message}`);
    }
  };

  const loadResearch = async () => {
    if (!selectedJob) return;

    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/interview-prep/companies/${encodeURIComponent(selectedJob.company_name)}/research`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setResearch(data.research);
      } else {
        setResearch(null);
      }
    } catch (err) {
      setResearch(null);
    }
  };

  const generateResearch = async () => {
    if (!selectedJob) return;

    const token = getToken();
    if (!token) return;

    try {
      setGeneratingResearch(true);
      const response = await fetch(`${API_URL}/api/interview-prep/companies/${encodeURIComponent(selectedJob.company_name)}/research`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate company research');
      }

      const data = await response.json();
      setResearch(data.research);

      // Refresh credit display to show updated count (only if not cached)
      if (!data.cached) {
        triggerCreditRefresh();
      }

      // Reload jobs to update status
      await loadJobs();

      if (data.cached) {
        showSuccess('Company research loaded from cache!');
      } else {
        showSuccess('Successfully generated company research!');
      }
    } catch (err) {
      showError(err.message);
    } finally {
      setGeneratingResearch(false);
    }
  };

  const handleEditStory = (story) => {
    setEditingStory(story.id);
    setEditForm({
      title: story.title,
      situation: story.situation,
      task: story.task,
      action: story.action,
      result: story.result,
      tags: story.tags.join(', ')
    });
  };

  const handleSaveEdit = (storyId) => {
    const updates = {
      title: editForm.title,
      situation: editForm.situation,
      task: editForm.task,
      action: editForm.action,
      result: editForm.result,
      tags: editForm.tags.split(',').map(t => t.trim()).filter(t => t)
    };
    updateStory(storyId, updates);
  };

  // Practice walkthrough functions
  const loadPracticeSessions = async () => {
    if (!selectedJob) return;

    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/interview-prep/applications/${selectedJob.id}/practice/sessions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSessionHistory(data.sessions || []);
      } else {
        setSessionHistory([]);
      }
    } catch (err) {
      setSessionHistory([]);
    }
  };

  const startNewPractice = async (mode = 'coach') => {
    if (!selectedJob) return;

    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/interview-prep/applications/${selectedJob.id}/practice/start?practice_mode=${mode}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to start practice session');
      }

      const data = await response.json();

      // Refresh credit display to show updated count
      triggerCreditRefresh();

      // Load resume PDF
      const pdfResponse = await fetch(`${API_URL}/resume/download/${selectedJob.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (pdfResponse.ok) {
        const blob = await pdfResponse.blob();
        const url = URL.createObjectURL(blob);
        setResumePdfUrl(url);
      }

      // Initialize active session
      setActiveSession({
        session_id: data.session_id,
        company_name: selectedJob.company_name,
        job_role: selectedJob.job_role,
        mode: mode
      });
      setPracticeMessages([
        { role: 'assistant', content: data.initial_message }
      ]);
      setTotalTime(0);
      setLapTime(0);
      setIsFinalFeedback(false);
      setFinalEvaluation(null);
      setPracticeView('active');
    } catch (err) {
      showError(err.message);
    }
  };

  const sendPracticeMessage = async () => {
    if (!practiceInput.trim() || !activeSession) return;

    const token = getToken();
    if (!token) return;

    try {
      setPracticeSending(true);

      const currentLapTime = lapTime;
      const userMessage = practiceInput.trim();

      // Add user message to UI immediately
      setPracticeMessages(prev => [...prev, {
        role: 'user',
        content: userMessage,
        lapTime: currentLapTime
      }]);
      setPracticeInput('');
      setLapTime(0); // Reset lap timer

      // Send to backend
      const response = await fetch(`${API_URL}/api/interview-prep/practice/${activeSession.session_id}/message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: userMessage,
          lap_duration_seconds: currentLapTime
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const data = await response.json();

      // Add AI response
      setPracticeMessages(prev => [...prev, {
        role: 'assistant',
        content: data.ai_response
      }]);

      // Check if final feedback
      if (data.is_final_feedback) {
        setIsFinalFeedback(true);

        // Load final evaluation scores
        const evalResponse = await fetch(`${API_URL}/api/interview-prep/practice/${activeSession.session_id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (evalResponse.ok) {
          const evalData = await evalResponse.json();
          if (evalData.overall_score) {
            setFinalEvaluation({
              overall_score: evalData.overall_score,
              clarity_score: evalData.clarity_score,
              role_fit_score: evalData.role_fit_score,
              technical_credibility_score: evalData.technical_credibility_score,
              confidence_score: evalData.confidence_score,
              strengths: evalData.strengths,
              top_fixes: evalData.top_fixes
            });
          }
        }
      }
    } catch (err) {
      showError(err.message);
    } finally {
      setPracticeSending(false);
    }
  };

  const completePractice = async () => {
    if (!activeSession) return;

    // Stop the timer immediately
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/interview-prep/practice/${activeSession.session_id}/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        showSuccess('Practice session saved!');
        // Reset and return to history
        setActiveSession(null);
        setPracticeMessages([]);
        setTotalTime(0);
        setLapTime(0);
        setIsFinalFeedback(false);
        setResumePdfUrl(null);
        setPracticeView('history');
        loadPracticeSessions();
      }
    } catch (err) {
      showError('Failed to save practice session');
    }
  };

  const viewPracticeSession = async (sessionId) => {
    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/interview-prep/practice/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setViewingSession(data);
        setPracticeView('viewing');
      }
    } catch (err) {
      showError('Failed to load practice session');
    }
  };

  const deletePracticeSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this practice session? This action cannot be undone.')) {
      return;
    }

    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/interview-prep/practice/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Remove session from local state
        setSessionHistory(prev => prev.filter(s => s.id !== sessionId));
      } else {
        throw new Error('Failed to delete session');
      }
    } catch (err) {
      showError('Failed to delete practice session');
    }
  };

  const startOverPractice = () => {
    if (window.confirm('Are you sure you want to start over? This will begin a new practice session.')) {
      const currentMode = activeSession?.mode || 'coach';
      startNewPractice(currentMode);
    }
  };

  // Timer effect for active practice session
  useEffect(() => {
    if (activeSession && practiceView === 'active') {
      timerIntervalRef.current = setInterval(() => {
        setTotalTime(prev => prev + 1);
        setLapTime(prev => prev + 1);
      }, 1000);

      return () => {
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
      };
    }
  }, [activeSession, practiceView]);

  // Helper function to format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper function to get lap timer color
  const getLapTimerColor = (seconds) => {
    if (seconds < 60) return '#22c55e'; // Green
    if (seconds < 90) return '#fbbf24'; // Yellow
    if (seconds < 120) return '#fb923c'; // Orange
    return '#ef4444'; // Red
  };

  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-primary)' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚡</div>
          <p style={{ color: 'var(--text-tertiary)' }}>Loading Interview Prep...</p>
        </div>
      </div>
    );
  }

  if (!user || !session) {
    navigate('/login');
    return null;
  }

  // Full-screen Active Practice View
  if (practiceView === 'active' && activeSession) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-base)', color: 'var(--text-primary)' }}>
        {/* Header */}
        <div style={{
          padding: '20px 40px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'var(--bg-surface)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div>
              <h1 style={{ fontSize: '24px', margin: 0 }}>{activeSession.company_name} - {activeSession.job_role}</h1>
              <p style={{ color: 'var(--text-tertiary)', margin: '4px 0 0 0', fontSize: '14px' }}>
                Practice Resume Walkthrough
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>
                Total Time
              </div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>
                {formatTime(totalTime)}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>
                Current Response
              </div>
              <div style={{
                fontSize: '24px',
                fontWeight: '700',
                color: getLapTimerColor(lapTime)
              }}>
                {formatTime(lapTime)}
              </div>
            </div>
            <button
              onClick={startOverPractice}
              disabled={practiceSending}
              style={{
                padding: '10px 20px',
                background: 'var(--bg-elevated)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-light)',
                borderRadius: '8px',
                cursor: practiceSending ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              Start Over
            </button>
            <button
              onClick={() => {
                if (isFinalFeedback) {
                  completePractice();
                } else {
                  if (window.confirm('End practice now?')) {
                    completePractice();
                  }
                }
              }}
              style={{
                padding: '10px 20px',
                background: isFinalFeedback
                  ? 'var(--gradient-primary)'
                  : 'rgba(147, 51, 234, 0.2)',
                color: isFinalFeedback ? '#fff' : 'var(--indigo-500)',
                border: isFinalFeedback ? 'none' : '1px solid rgba(99, 102, 241, 0.3)',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              End Practice
            </button>
          </div>
        </div>

        {/* Main Content - Resume PDF + Chat */}
        <ResizablePanels defaultSizes={[50, 50]}>
          {/* Left Panel - Resume PDF */}
          <div style={{
            height: 'calc(100vh - 80px)',
            overflowY: 'auto',
            background: 'var(--bg-base)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {resumePdfUrl ? (
              <iframe
                src={resumePdfUrl}
                title="Resume PDF"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
              />
            ) : (
              <div style={{ color: 'var(--text-tertiary)', textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
                <p>Loading resume...</p>
              </div>
            )}
          </div>

          {/* Right Panel - Chat Interface */}
          <div style={{
            height: 'calc(100vh - 80px)',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--bg-base)',
            padding: '20px'
          }}>
            {/* Messages */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              marginBottom: '20px',
              paddingRight: '10px'
            }}>
              {practiceMessages.map((msg, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: '16px',
                    display: 'flex',
                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
                  }}
                >
                  <div style={{
                    maxWidth: '85%',
                    background: msg.role === 'user'
                      ? 'rgba(147, 51, 234, 0.2)'
                      : 'rgba(255, 255, 255, 0.1)',
                    border: `1px solid ${msg.role === 'user' ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255, 255, 255, 0.1)'}`,
                    borderRadius: '12px',
                    padding: '12px 16px'
                  }}>
                    <div style={{ fontSize: '14px', color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>
                      {msg.content}
                    </div>
                    {msg.role === 'user' && msg.lapTime !== undefined && (
                      <div style={{
                        fontSize: '12px',
                        color: getLapTimerColor(msg.lapTime),
                        marginTop: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        ⏱️ {formatTime(msg.lapTime)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {practiceSending && (
                <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '16px' }}>
                  <div style={{
                    background: 'var(--bg-elevated)',
                    border: '1px solid var(--border-light)',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    color: 'var(--text-tertiary)',
                    fontSize: '14px'
                  }}>
                    AI is thinking...
                  </div>
                </div>
              )}

              {/* Final Evaluation Scorecard */}
              {isFinalFeedback && finalEvaluation && (
                <div style={{
                  background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.2) 0%, rgba(107, 70, 193, 0.2) 100%)',
                  border: '2px solid rgba(147, 51, 234, 0.4)',
                  borderRadius: '16px',
                  padding: '24px',
                  marginTop: '20px'
                }}>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '20px', textAlign: 'center' }}>
                    📊 Final Evaluation
                  </div>

                  {/* Overall Score */}
                  <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <div style={{ fontSize: '48px', fontWeight: '700', color: 'var(--indigo-500)', marginBottom: '8px' }}>
                      {finalEvaluation.overall_score}/10
                    </div>
                    <div style={{ fontSize: '14px', color: 'var(--text-tertiary)' }}>Overall Score</div>
                  </div>

                  {/* Dimension Scores */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                    <div style={{ background: 'var(--bg-elevated)', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>{finalEvaluation.clarity_score}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Clarity</div>
                    </div>
                    <div style={{ background: 'var(--bg-elevated)', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>{finalEvaluation.role_fit_score}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Role Fit</div>
                    </div>
                    <div style={{ background: 'var(--bg-elevated)', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>{finalEvaluation.technical_credibility_score}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Tech Depth</div>
                    </div>
                    <div style={{ background: 'var(--bg-elevated)', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>{finalEvaluation.confidence_score}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Confidence</div>
                    </div>
                  </div>

                  {/* Strengths */}
                  {finalEvaluation.strengths && finalEvaluation.strengths.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#22c55e', marginBottom: '12px' }}>
                        ✓ Strengths
                      </div>
                      <ul style={{ marginLeft: '20px', color: '#d1d1d1', lineHeight: '1.8' }}>
                        {finalEvaluation.strengths.map((strength, i) => (
                          <li key={i} style={{ fontSize: '14px' }}>{strength}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Top Fixes */}
                  {finalEvaluation.top_fixes && finalEvaluation.top_fixes.length > 0 && (
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#fbbf24', marginBottom: '12px' }}>
                        ⚡ Top Fixes to Improve
                      </div>
                      <ul style={{ marginLeft: '20px', color: '#d1d1d1', lineHeight: '1.8' }}>
                        {finalEvaluation.top_fixes.map((fix, i) => (
                          <li key={i} style={{ fontSize: '14px' }}>{fix}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Input area - ALWAYS visible so users can continue chatting */}
            <div style={{
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-light)',
              borderRadius: '12px',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {isFinalFeedback && (
                <div style={{
                  background: 'rgba(34, 197, 94, 0.1)',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '8px',
                  textAlign: 'center'
                }}>
                  <p style={{ margin: 0, fontSize: '14px', color: '#22c55e' }}>
                    ✓ You received your evaluation! Feel free to ask follow-up questions or click "End Practice" when ready.
                  </p>
                </div>
              )}
              <textarea
                value={practiceInput}
                onChange={(e) => setPracticeInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    sendPracticeMessage();
                  }
                }}
                placeholder={isFinalFeedback
                  ? "Ask follow-up questions or discuss the feedback... (Cmd/Ctrl + Enter to send)"
                  : "Type your response... (Cmd/Ctrl + Enter to send)"}
                disabled={practiceSending}
                style={{
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border-light)',
                  borderRadius: '8px',
                  padding: '12px',
                  color: 'var(--text-primary)',
                  fontSize: '14px',
                  resize: 'vertical',
                  minHeight: '100px',
                  fontFamily: 'inherit'
                }}
              />
              <button
                onClick={sendPracticeMessage}
                disabled={!practiceInput.trim() || practiceSending}
                style={{
                  padding: '12px 24px',
                  background: (!practiceInput.trim() || practiceSending)
                    ? 'rgba(99, 102, 241, 0.3)'
                    : 'var(--gradient-primary)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (!practiceInput.trim() || practiceSending) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                {practiceSending ? 'Sending...' : 'Send Response'}
              </button>
            </div>
          </div>
        </ResizablePanels>
      </div>
    );
  }

  return (
    <DashboardLayout
      title="Interview Prep"
      subtitle="Prepare with AI-powered coaching"
    >
      {/* Main Content Area with Job Sidebar */}
      <div style={{ display: 'flex', gap: '24px', minHeight: 'calc(100vh - 200px)' }}>
        {/* Left Sidebar - Job List */}
        <div style={{
          width: '350px',
          flexShrink: 0,
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-light)',
          borderRadius: 'var(--radius-lg)',
          padding: '24px',
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 200px)',
          position: 'sticky',
          top: '24px'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>
            Interview Pipeline
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--text-tertiary)', marginBottom: '24px' }}>
            {jobs.length} {jobs.length === 1 ? 'interview' : 'interviews'} scheduled
          </p>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-tertiary)' }}>
              Loading...
            </div>
          ) : jobs.length === 0 ? (
            <div style={{
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-light)',
              borderRadius: '12px',
              padding: '24px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
              <p style={{ color: 'var(--text-tertiary)', fontSize: '14px', marginBottom: '12px' }}>
                No interviews scheduled yet
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                Mark jobs as "Interview" or "Phone Screen" in your Dashboard to prepare here
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  marginTop: '16px',
                  padding: '8px 16px',
                  background: 'var(--gradient-primary)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Go to Dashboard
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {jobs.map(job => (
                <div
                  key={job.id}
                  onClick={() => setSelectedJob(job)}
                  style={{
                    background: selectedJob?.id === job.id
                      ? 'rgba(147, 51, 234, 0.2)'
                      : 'rgba(255, 255, 255, 0.05)',
                    border: selectedJob?.id === job.id
                      ? '1px solid rgba(147, 51, 234, 0.5)'
                      : '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    padding: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    if (selectedJob?.id !== job.id) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (selectedJob?.id !== job.id) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px', color: 'var(--text-primary)' }}>
                    {job.company_name}
                  </div>
                  <div style={{ fontSize: '14px', color: 'var(--text-tertiary)', marginBottom: '12px' }}>
                    {job.job_role}
                  </div>

                  {/* Status badges */}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{
                      padding: '4px 8px',
                      background: job.status === 'Interview'
                        ? 'rgba(251, 191, 36, 0.2)'
                        : 'rgba(251, 191, 36, 0.2)',
                      color: '#fbbf24',
                      border: '1px solid rgba(251, 191, 36, 0.5)',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {job.status}
                    </span>
                    {job.has_star_stories && (
                      <span style={{
                        padding: '4px 8px',
                        background: 'rgba(34, 197, 94, 0.2)',
                        color: '#22c55e',
                        border: '1px solid rgba(34, 197, 94, 0.5)',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {job.star_story_count} Stories
                      </span>
                    )}
                    {job.has_company_research && (
                      <span style={{
                        padding: '4px 8px',
                        background: 'rgba(96, 165, 250, 0.2)',
                        color: '#60a5fa',
                        border: '1px solid rgba(96, 165, 250, 0.5)',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        Research ✓
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Main Content Area - Tabs */}
        <div style={{
          flex: 1,
          padding: '24px',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-light)',
          borderRadius: 'var(--radius-lg)',
          minHeight: 'fit-content'
        }}>
          {!selectedJob ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'var(--text-tertiary)'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '64px', marginBottom: '16px' }}>🎯</div>
                <p style={{ fontSize: '18px' }}>Select a job to start preparing</p>
              </div>
            </div>
          ) : (
            <>
              {/* Job Header */}
              <div style={{ marginBottom: '32px' }}>
                <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '8px', color: 'var(--text-primary)' }}>
                  {selectedJob.company_name}
                </h1>
                <p style={{ fontSize: '18px', color: 'var(--text-tertiary)', marginBottom: '16px' }}>
                  {selectedJob.job_role}
                </p>
                {selectedJob.job_listing_url && (
                  <a
                    href={selectedJob.job_listing_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: 'var(--indigo-500)',
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    View Job Posting →
                  </a>
                )}
              </div>

              {/* Tabs */}
              <div style={{
                display: 'flex',
                gap: '8px',
                padding: '8px',
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-light)',
                borderRadius: 'var(--radius-lg)',
                marginBottom: '32px'
              }}>
                <button
                  onClick={() => setActiveTab('star')}
                  style={{
                    padding: '12px 24px',
                    background: activeTab === 'star' ? 'var(--bg-elevated)' : 'transparent',
                    color: activeTab === 'star' ? 'var(--text-primary)' : 'var(--text-tertiary)',
                    border: activeTab === 'star' ? '1px solid var(--indigo-500)' : '1px solid transparent',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s'
                  }}
                >
                  STAR Stories
                </button>
                <button
                  onClick={() => setActiveTab('research')}
                  style={{
                    padding: '12px 24px',
                    background: activeTab === 'research' ? 'var(--bg-elevated)' : 'transparent',
                    color: activeTab === 'research' ? 'var(--text-primary)' : 'var(--text-tertiary)',
                    border: activeTab === 'research' ? '1px solid var(--indigo-500)' : '1px solid transparent',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s'
                  }}
                >
                  Company Research
                </button>
                <button
                  onClick={() => {
                    setActiveTab('practice');
                    setPracticeView('history');
                  }}
                  style={{
                    padding: '12px 24px',
                    background: activeTab === 'practice' ? 'var(--bg-elevated)' : 'transparent',
                    color: activeTab === 'practice' ? 'var(--text-primary)' : 'var(--text-tertiary)',
                    border: activeTab === 'practice' ? '1px solid var(--indigo-500)' : '1px solid transparent',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s'
                  }}
                >
                  Practice Walkthrough
                </button>
                <button
                  onClick={() => setActiveTab('mock-interview')}
                  style={{
                    padding: '12px 24px',
                    background: activeTab === 'mock-interview' ? 'var(--bg-elevated)' : 'transparent',
                    color: activeTab === 'mock-interview' ? 'var(--text-primary)' : 'var(--text-tertiary)',
                    border: activeTab === 'mock-interview' ? '1px solid var(--indigo-500)' : '1px solid transparent',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s'
                  }}
                >
                  Mock Interview
                </button>
              </div>

              {/* Tab Content */}
              {activeTab === 'star' && (
                <div>
                  {stories.length === 0 ? (
                    <div style={{
                      background: 'var(--bg-elevated)',
                      border: '1px solid var(--border-light)',
                      borderRadius: '12px',
                      padding: '48px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '64px', marginBottom: '16px' }}>✨</div>
                      <h3 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px', color: 'var(--text-primary)' }}>
                        Generate STAR Stories
                      </h3>
                      <p style={{ color: 'var(--text-tertiary)', fontSize: '16px', marginBottom: '24px', maxWidth: '500px', margin: '0 auto 24px' }}>
                        Create 10 compelling STAR format interview stories tailored to this role using your master resume and the job description.
                      </p>
                      <button
                        onClick={generateStories}
                        disabled={generatingStories}
                        style={{
                          padding: '14px 32px',
                          background: generatingStories
                            ? 'rgba(99, 102, 241, 0.3)'
                            : 'var(--gradient-primary)',
                          color: 'var(--text-primary)',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: generatingStories ? 'not-allowed' : 'pointer',
                          fontSize: '16px',
                          fontWeight: '600',
                          transition: 'all 0.2s'
                        }}
                      >
                        {generatingStories ? 'Generating Stories...' : 'Generate 10 STAR Stories'}
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '8px'
                      }}>
                        <h3 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)' }}>
                          Your STAR Stories ({stories.length})
                        </h3>
                      </div>

                      {stories.map((story, idx) => (
                        <div
                          key={story.id}
                          style={{
                            background: 'var(--bg-elevated)',
                            border: '1px solid var(--border-light)',
                            borderRadius: '12px',
                            padding: '24px',
                            transition: 'all 0.2s'
                          }}
                        >
                          {editingStory === story.id ? (
                            // Edit Mode
                            <div>
                              <input
                                type="text"
                                value={editForm.title}
                                onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                                style={{
                                  width: '100%',
                                  padding: '12px',
                                  background: 'var(--bg-elevated)',
                                  border: '1px solid rgba(255, 255, 255, 0.2)',
                                  borderRadius: '8px',
                                  color: 'var(--text-primary)',
                                  fontSize: '18px',
                                  fontWeight: '600',
                                  marginBottom: '16px'
                                }}
                                placeholder="Story Title"
                              />

                              <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--indigo-500)', marginBottom: '8px' }}>
                                  Situation
                                </label>
                                <textarea
                                  value={editForm.situation}
                                  onChange={(e) => setEditForm({...editForm, situation: e.target.value})}
                                  style={{
                                    width: '100%',
                                    padding: '12px',
                                    background: 'var(--bg-elevated)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    borderRadius: '8px',
                                    color: 'var(--text-primary)',
                                    fontSize: '14px',
                                    minHeight: '80px',
                                    resize: 'vertical'
                                  }}
                                />
                              </div>

                              <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--indigo-500)', marginBottom: '8px' }}>
                                  Task
                                </label>
                                <textarea
                                  value={editForm.task}
                                  onChange={(e) => setEditForm({...editForm, task: e.target.value})}
                                  style={{
                                    width: '100%',
                                    padding: '12px',
                                    background: 'var(--bg-elevated)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    borderRadius: '8px',
                                    color: 'var(--text-primary)',
                                    fontSize: '14px',
                                    minHeight: '80px',
                                    resize: 'vertical'
                                  }}
                                />
                              </div>

                              <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--indigo-500)', marginBottom: '8px' }}>
                                  Action
                                </label>
                                <textarea
                                  value={editForm.action}
                                  onChange={(e) => setEditForm({...editForm, action: e.target.value})}
                                  style={{
                                    width: '100%',
                                    padding: '12px',
                                    background: 'var(--bg-elevated)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    borderRadius: '8px',
                                    color: 'var(--text-primary)',
                                    fontSize: '14px',
                                    minHeight: '120px',
                                    resize: 'vertical'
                                  }}
                                />
                              </div>

                              <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--indigo-500)', marginBottom: '8px' }}>
                                  Result
                                </label>
                                <textarea
                                  value={editForm.result}
                                  onChange={(e) => setEditForm({...editForm, result: e.target.value})}
                                  style={{
                                    width: '100%',
                                    padding: '12px',
                                    background: 'var(--bg-elevated)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    borderRadius: '8px',
                                    color: 'var(--text-primary)',
                                    fontSize: '14px',
                                    minHeight: '80px',
                                    resize: 'vertical'
                                  }}
                                />
                              </div>

                              <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--indigo-500)', marginBottom: '8px' }}>
                                  Tags (comma separated)
                                </label>
                                <input
                                  type="text"
                                  value={editForm.tags}
                                  onChange={(e) => setEditForm({...editForm, tags: e.target.value})}
                                  style={{
                                    width: '100%',
                                    padding: '12px',
                                    background: 'var(--bg-elevated)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    borderRadius: '8px',
                                    color: 'var(--text-primary)',
                                    fontSize: '14px'
                                  }}
                                  placeholder="leadership, python, teamwork"
                                />
                              </div>

                              <div style={{ display: 'flex', gap: '12px' }}>
                                <button
                                  onClick={() => handleSaveEdit(story.id)}
                                  style={{
                                    padding: '10px 20px',
                                    background: 'var(--gradient-primary)',
                                    color: 'var(--text-primary)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                  }}
                                >
                                  Save Changes
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingStory(null);
                                    setEditForm({});
                                  }}
                                  style={{
                                    padding: '10px 20px',
                                    background: 'var(--bg-elevated)',
                                    color: 'var(--text-tertiary)',
                                    border: '1px solid var(--border-light)',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            // View Mode
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                <div>
                                  <h4 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>
                                    {idx + 1}. {story.title}
                                  </h4>
                                  {story.tags && story.tags.length > 0 && (
                                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                      {story.tags.map((tag, i) => (
                                        <span
                                          key={i}
                                          style={{
                                            padding: '4px 8px',
                                            background: 'rgba(147, 51, 234, 0.2)',
                                            color: 'var(--indigo-500)',
                                            border: '1px solid rgba(99, 102, 241, 0.3)',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            fontWeight: '600'
                                          }}
                                        >
                                          {tag}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <button
                                  onClick={() => handleEditStory(story)}
                                  style={{
                                    padding: '6px 12px',
                                    background: 'rgba(147, 51, 234, 0.2)',
                                    color: 'var(--indigo-500)',
                                    border: '1px solid rgba(99, 102, 241, 0.3)',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: '600'
                                  }}
                                >
                                  Edit
                                </button>
                              </div>

                              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <div>
                                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--indigo-500)', marginBottom: '6px' }}>
                                    Situation
                                  </div>
                                  <div style={{ fontSize: '14px', color: '#d1d1d1', lineHeight: '1.6' }}>
                                    {story.situation}
                                  </div>
                                </div>

                                <div>
                                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--indigo-500)', marginBottom: '6px' }}>
                                    Task
                                  </div>
                                  <div style={{ fontSize: '14px', color: '#d1d1d1', lineHeight: '1.6' }}>
                                    {story.task}
                                  </div>
                                </div>

                                <div>
                                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--indigo-500)', marginBottom: '6px' }}>
                                    Action
                                  </div>
                                  <div style={{ fontSize: '14px', color: '#d1d1d1', lineHeight: '1.6' }}>
                                    {story.action}
                                  </div>
                                </div>

                                <div>
                                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--indigo-500)', marginBottom: '6px' }}>
                                    Result
                                  </div>
                                  <div style={{ fontSize: '14px', color: '#d1d1d1', lineHeight: '1.6' }}>
                                    {story.result}
                                  </div>
                                </div>
                              </div>

                              {story.is_user_edited && (
                                <div style={{
                                  marginTop: '12px',
                                  padding: '8px 12px',
                                  background: 'rgba(96, 165, 250, 0.1)',
                                  border: '1px solid rgba(96, 165, 250, 0.3)',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  color: '#60a5fa'
                                }}>
                                  ✏️ Edited by you
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'research' && (
                <div>
                  {!research ? (
                    <div style={{
                      background: 'var(--bg-elevated)',
                      border: '1px solid var(--border-light)',
                      borderRadius: '12px',
                      padding: '48px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '64px', marginBottom: '16px' }}>🔍</div>
                      <h3 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px', color: 'var(--text-primary)' }}>
                        Research {selectedJob.company_name}
                      </h3>
                      <p style={{ color: 'var(--text-tertiary)', fontSize: '16px', marginBottom: '24px', maxWidth: '500px', margin: '0 auto 24px' }}>
                        Generate comprehensive company research including culture, recent news, products, and values.
                      </p>
                      <button
                        onClick={generateResearch}
                        disabled={generatingResearch}
                        style={{
                          padding: '14px 32px',
                          background: generatingResearch
                            ? 'rgba(99, 102, 241, 0.3)'
                            : 'var(--gradient-primary)',
                          color: 'var(--text-primary)',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: generatingResearch ? 'not-allowed' : 'pointer',
                          fontSize: '16px',
                          fontWeight: '600',
                          transition: 'all 0.2s'
                        }}
                      >
                        {generatingResearch ? 'Researching...' : 'Generate Company Research'}
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                      {/* Refresh Button */}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '-8px' }}>
                        <button
                          onClick={async () => {
                            if (window.confirm('This will delete the existing research and generate new data. Continue?')) {
                              const token = getToken();
                              try {
                                // Delete cached research from database
                                await fetch(`${API_URL}/api/interview-prep/companies/${encodeURIComponent(selectedJob.company_name)}/research`, {
                                  method: 'DELETE',
                                  headers: {
                                    'Authorization': `Bearer ${token}`,
                                    'Content-Type': 'application/json'
                                  }
                                });
                              } catch (err) {
                                // Research may not exist yet - proceed to generate
                              }
                              setResearch(null);
                              await generateResearch();
                            }
                          }}
                          disabled={generatingResearch}
                          style={{
                            padding: '8px 16px',
                            background: 'rgba(147, 51, 234, 0.2)',
                            color: 'var(--indigo-500)',
                            border: '1px solid rgba(99, 102, 241, 0.3)',
                            borderRadius: '8px',
                            cursor: generatingResearch ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: '600',
                            transition: 'all 0.2s'
                          }}
                          onMouseOver={(e) => {
                            if (!generatingResearch) {
                              e.target.style.background = 'rgba(99, 102, 241, 0.3)';
                            }
                          }}
                          onMouseOut={(e) => {
                            e.target.style.background = 'rgba(147, 51, 234, 0.2)';
                          }}
                        >
                          🔄 Regenerate Research
                        </button>
                      </div>
                      {/* Culture Info */}
                      {research.culture_info && (
                        <div style={{
                          background: 'var(--bg-elevated)',
                          border: '1px solid var(--border-light)',
                          borderRadius: '12px',
                          padding: '24px'
                        }}>
                          <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>
                            🏢 Company Culture
                          </h3>
                          <p style={{ fontSize: '14px', color: '#d1d1d1', lineHeight: '1.6', marginBottom: '12px' }}>
                            {research.culture_info.summary}
                          </p>
                          {research.culture_info.values && research.culture_info.values.length > 0 && (
                            <div>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--indigo-500)', marginBottom: '8px' }}>
                                Core Values:
                              </div>
                              <ul style={{ marginLeft: '20px', color: '#d1d1d1' }}>
                                {research.culture_info.values.map((value, i) => (
                                  <li key={i} style={{ fontSize: '14px', marginBottom: '4px' }}>{value}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {research.culture_info.work_environment && (
                            <div style={{ marginTop: '12px' }}>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--indigo-500)', marginBottom: '8px' }}>
                                Work Environment:
                              </div>
                              <p style={{ fontSize: '14px', color: '#d1d1d1', lineHeight: '1.6' }}>
                                {research.culture_info.work_environment}
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Recent News */}
                      {research.recent_news && research.recent_news.length > 0 && (
                        <div style={{
                          background: 'var(--bg-elevated)',
                          border: '1px solid var(--border-light)',
                          borderRadius: '12px',
                          padding: '24px'
                        }}>
                          <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>
                            📰 Recent News
                          </h3>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {research.recent_news.map((news, i) => (
                              <div key={i} style={{
                                padding: '16px',
                                background: 'var(--bg-surface)',
                                borderRadius: '8px'
                              }}>
                                <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px' }}>
                                  {news.title}
                                </div>
                                <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>
                                  {news.date}
                                </div>
                                <p style={{ fontSize: '14px', color: '#d1d1d1', lineHeight: '1.6' }}>
                                  {news.summary}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Products & Services */}
                      {research.products_services && (
                        <div style={{
                          background: 'var(--bg-elevated)',
                          border: '1px solid var(--border-light)',
                          borderRadius: '12px',
                          padding: '24px'
                        }}>
                          <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>
                            🚀 Products & Services
                          </h3>
                          {research.products_services.main_products && research.products_services.main_products.length > 0 && (
                            <div style={{ marginBottom: '16px' }}>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--indigo-500)', marginBottom: '8px' }}>
                                Main Products:
                              </div>
                              <ul style={{ marginLeft: '20px', color: '#d1d1d1' }}>
                                {research.products_services.main_products.map((product, i) => (
                                  <li key={i} style={{ fontSize: '14px', marginBottom: '4px' }}>{product}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {research.products_services.key_services && research.products_services.key_services.length > 0 && (
                            <div style={{ marginBottom: '16px' }}>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--indigo-500)', marginBottom: '8px' }}>
                                Key Services:
                              </div>
                              <ul style={{ marginLeft: '20px', color: '#d1d1d1' }}>
                                {research.products_services.key_services.map((service, i) => (
                                  <li key={i} style={{ fontSize: '14px', marginBottom: '4px' }}>{service}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {research.products_services.markets && research.products_services.markets.length > 0 && (
                            <div>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--indigo-500)', marginBottom: '8px' }}>
                                Target Markets:
                              </div>
                              <ul style={{ marginLeft: '20px', color: '#d1d1d1' }}>
                                {research.products_services.markets.map((market, i) => (
                                  <li key={i} style={{ fontSize: '14px', marginBottom: '4px' }}>{market}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Company Values */}
                      {research.company_values && (
                        <div style={{
                          background: 'var(--bg-elevated)',
                          border: '1px solid var(--border-light)',
                          borderRadius: '12px',
                          padding: '24px'
                        }}>
                          <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>
                            💎 Mission & Values
                          </h3>
                          {research.company_values.mission && (
                            <div style={{ marginBottom: '16px' }}>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--indigo-500)', marginBottom: '8px' }}>
                                Mission:
                              </div>
                              <p style={{ fontSize: '14px', color: '#d1d1d1', lineHeight: '1.6' }}>
                                {research.company_values.mission}
                              </p>
                            </div>
                          )}
                          {research.company_values.vision && (
                            <div style={{ marginBottom: '16px' }}>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--indigo-500)', marginBottom: '8px' }}>
                                Vision:
                              </div>
                              <p style={{ fontSize: '14px', color: '#d1d1d1', lineHeight: '1.6' }}>
                                {research.company_values.vision}
                              </p>
                            </div>
                          )}
                          {research.company_values.core_values && research.company_values.core_values.length > 0 && (
                            <div>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--indigo-500)', marginBottom: '8px' }}>
                                Core Values:
                              </div>
                              <ul style={{ marginLeft: '20px', color: '#d1d1d1' }}>
                                {research.company_values.core_values.map((value, i) => (
                                  <li key={i} style={{ fontSize: '14px', marginBottom: '4px' }}>{value}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Additional Insights */}
                      {research.additional_insights && (
                        <div style={{
                          background: 'var(--bg-elevated)',
                          border: '1px solid var(--border-light)',
                          borderRadius: '12px',
                          padding: '24px'
                        }}>
                          <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>
                            📊 Additional Insights
                          </h3>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            {research.additional_insights.employee_count && (
                              <div>
                                <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>
                                  Company Size:
                                </div>
                                <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
                                  {research.additional_insights.employee_count}
                                </div>
                              </div>
                            )}
                            {research.additional_insights.funding && (
                              <div>
                                <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>
                                  Funding:
                                </div>
                                <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
                                  {research.additional_insights.funding}
                                </div>
                              </div>
                            )}
                            {research.additional_insights.industry && (
                              <div>
                                <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>
                                  Industry:
                                </div>
                                <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
                                  {research.additional_insights.industry}
                                </div>
                              </div>
                            )}
                            {research.additional_insights.headquarters && (
                              <div>
                                <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>
                                  Headquarters:
                                </div>
                                <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
                                  {research.additional_insights.headquarters}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Practice Resume Walkthrough Tab */}
              {activeTab === 'practice' && (
                <div>
                  {/* History View */}
                  {practiceView === 'history' && (
                    <div>
                      <div style={{
                        background: 'var(--bg-elevated)',
                        border: '1px solid var(--border-light)',
                        borderRadius: '12px',
                        padding: '32px',
                        marginBottom: '24px'
                      }}>
                        <h3 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px', color: 'var(--text-primary)' }}>
                          Practice Resume Walkthrough
                        </h3>
                        <p style={{ color: 'var(--text-tertiary)', fontSize: '16px', marginBottom: '24px' }}>
                          Practice explaining your resume with AI. Choose your mode below.
                        </p>

                        {/* Mode Selection */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                          {/* Coach Mode */}
                          <div style={{
                            background: 'rgba(34, 197, 94, 0.1)',
                            border: '2px solid rgba(34, 197, 94, 0.3)',
                            borderRadius: '12px',
                            padding: '20px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.background = 'rgba(34, 197, 94, 0.15)';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.background = 'rgba(34, 197, 94, 0.1)';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: '#22c55e', marginBottom: '8px' }}>
                              🎓 Coach Mode
                            </div>
                            <p style={{ fontSize: '14px', color: 'var(--text-tertiary)', marginBottom: '16px', lineHeight: '1.5' }}>
                              Get helpful feedback during the session. AI explains what's strong or weak and suggests improvements in real-time.
                            </p>
                            <button
                              onClick={() => startNewPractice('coach')}
                              style={{
                                width: '100%',
                                padding: '12px 24px',
                                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                                color: 'var(--text-primary)',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '15px',
                                fontWeight: '600'
                              }}
                            >
                              Start Coach Session
                            </button>
                          </div>

                          {/* Interview Mode */}
                          <div style={{
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: '2px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: '12px',
                            padding: '20px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: '#ef4444', marginBottom: '8px' }}>
                              💼 Interview Mode
                            </div>
                            <p style={{ fontSize: '14px', color: 'var(--text-tertiary)', marginBottom: '16px', lineHeight: '1.5' }}>
                              Realistic interview simulation. No coaching during—all feedback saved for the end. Test yourself under real conditions.
                            </p>
                            <button
                              onClick={() => startNewPractice('interview')}
                              style={{
                                width: '100%',
                                padding: '12px 24px',
                                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                color: 'var(--text-primary)',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '15px',
                                fontWeight: '600'
                              }}
                            >
                              Start Interview Simulation
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Session History */}
                      {sessionHistory.length > 0 && (
                        <div>
                          <h4 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary)' }}>
                            Previous Sessions
                          </h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {sessionHistory.map((session, index) => (
                              <div
                                key={session.id}
                                style={{
                                  background: 'var(--bg-elevated)',
                                  border: '1px solid var(--border-light)',
                                  borderRadius: '8px',
                                  padding: '16px',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}
                              >
                                <div>
                                  <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>
                                    Session #{sessionHistory.length - index}
                                  </div>
                                  <div style={{ fontSize: '14px', color: 'var(--text-tertiary)' }}>
                                    {new Date(session.created_at).toLocaleString('en-US', {
                                      month: 'long',
                                      day: 'numeric',
                                      year: 'numeric',
                                      hour: 'numeric',
                                      minute: '2-digit'
                                    })}
                                  </div>
                                  {session.total_duration_seconds && (
                                    <div style={{ fontSize: '14px', color: 'var(--indigo-500)', marginTop: '4px' }}>
                                      Duration: {Math.floor(session.total_duration_seconds / 60)}m {session.total_duration_seconds % 60}s
                                    </div>
                                  )}
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <button
                                    onClick={() => viewPracticeSession(session.id)}
                                    style={{
                                      padding: '8px 16px',
                                      background: 'rgba(147, 51, 234, 0.2)',
                                      color: 'var(--indigo-500)',
                                      border: '1px solid rgba(99, 102, 241, 0.3)',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '14px',
                                      fontWeight: '600'
                                    }}
                                  >
                                    View Session
                                  </button>
                                  <button
                                    onClick={() => deletePracticeSession(session.id)}
                                    style={{
                                      padding: '8px 16px',
                                      background: 'rgba(239, 68, 68, 0.2)',
                                      color: '#ef4444',
                                      border: '1px solid rgba(239, 68, 68, 0.3)',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '14px',
                                      fontWeight: '600'
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Viewing Past Session */}
                  {practiceView === 'viewing' && viewingSession && (
                    <div>
                      <button
                        onClick={() => setPracticeView('history')}
                        style={{
                          padding: '8px 16px',
                          background: 'var(--bg-elevated)',
                          color: 'var(--text-primary)',
                          border: '1px solid var(--border-light)',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          marginBottom: '16px'
                        }}
                      >
                        ← Back to Sessions
                      </button>

                      {/* Session info */}
                      <div style={{
                        background: 'var(--bg-elevated)',
                        border: '1px solid var(--border-light)',
                        borderRadius: '12px',
                        padding: '20px',
                        marginBottom: '16px'
                      }}>
                        <h3 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '8px' }}>
                          {viewingSession.company_name} - {viewingSession.job_role}
                        </h3>
                        <div style={{ fontSize: '14px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>
                          {new Date(viewingSession.created_at).toLocaleString()}
                        </div>
                        {viewingSession.total_duration_seconds && (
                          <div style={{ fontSize: '14px', color: 'var(--indigo-500)' }}>
                            Total Duration: {Math.floor(viewingSession.total_duration_seconds / 60)}m {viewingSession.total_duration_seconds % 60}s
                          </div>
                        )}
                      </div>

                      {/* Transcript */}
                      <div style={{
                        background: 'var(--bg-elevated)',
                        border: '1px solid var(--border-light)',
                        borderRadius: '12px',
                        padding: '20px'
                      }}>
                        <h4 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '16px' }}>
                          Conversation Transcript
                        </h4>
                        {viewingSession.messages.map((msg, index) => (
                          <div
                            key={index}
                            style={{
                              marginBottom: '16px',
                              display: 'flex',
                              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
                            }}
                          >
                            <div style={{
                              maxWidth: '70%',
                              background: msg.role === 'user'
                                ? 'rgba(147, 51, 234, 0.2)'
                                : 'rgba(255, 255, 255, 0.1)',
                              border: `1px solid ${msg.role === 'user' ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255, 255, 255, 0.1)'}`,
                              borderRadius: '12px',
                              padding: '12px 16px'
                            }}>
                              <div style={{ fontSize: '14px', color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>
                                {msg.content}
                              </div>
                              {msg.role === 'user' && msg.lap_duration_seconds && (
                                <div style={{
                                  fontSize: '12px',
                                  color: getLapTimerColor(msg.lap_duration_seconds),
                                  marginTop: '8px'
                                }}>
                                  ⏱️ {formatTime(msg.lap_duration_seconds)}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Mock Interview Tab */}
              {activeTab === 'mock-interview' && (
                <div style={{
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border-light)',
                  borderRadius: '12px',
                  padding: '48px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '64px', marginBottom: '16px' }}>🎯</div>
                  <h3 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px', color: 'var(--text-primary)' }}>
                    Full Mock Interview
                  </h3>
                  <p style={{ color: 'var(--text-tertiary)', fontSize: '16px', marginBottom: '24px', maxWidth: '500px', margin: '0 auto 24px' }}>
                    Practice a complete interview simulation with FSM-based question flow and harsh, honest AI feedback.
                  </p>
                  <button
                    onClick={() => navigate(`/mock-interview/setup?appId=${selectedJob.id}`)}
                    style={{
                      padding: '14px 32px',
                      background: 'var(--gradient-primary)',
                      color: 'var(--text-primary)',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '600',
                      transition: 'all 0.2s'
                    }}
                  >
                    Start Mock Interview
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default InterviewPrep;
